<template>
  <v-dialog v-model="dialog" @keydown.esc="resolveCancel" scrollable max-width="85vw">
    <v-card height="85vh">
      <v-toolbar color="surface-variant">
        <v-toolbar-title>{{ title ?? $t('admin-resources.resourceLookup') }}</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-4">
        <div v-show="!!message" v-html="message"></div>

        <div>
          <v-data-table-server
            :sort-by="[{ key: 'createdTimestamp', order: 'desc' }]"
            item-value="uuid"
            show-select
            select-strategy="single"
            v-model="selectedResource"
            :headers="headers"
            :items="resources"
            v-model:options="resourcesTableOptions"
            :items-length="totalResources"
            :loading="loading"
            :items-per-page-options="[10, 20, 30]"
            class="elevation-1"
          >
            <template v-slot:top>
              <!-- Saved filters dialog -->
              <v-dialog v-model="savedFiltersDialog" max-width="600px" scrollable>
                <v-card>
                  <v-toolbar color="surface-variant" density="compact">
                    <v-toolbar-title>{{ $t('admin-resources.filter.savedFilters') }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="savedFiltersDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                  </v-toolbar>
                  <v-card-text class="pa-3 pb-0" style="flex-shrink: 0">
                    <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                      {{ $t('admin-resources.filter.savedFilters.overwriteWarning') }}
                    </v-alert>
                    <v-text-field
                      v-model="savedFiltersSearch"
                      clearable
                      hide-details
                      density="compact"
                      prepend-inner-icon="mdi-magnify"
                      :label="$t('admin-resources.filter.savedFilters.search')"
                    ></v-text-field>
                  </v-card-text>
                  <v-list style="max-height: 400px; overflow-y: auto;">
                    <v-list-item
                      v-for="filter in filteredMenuFilters"
                      :key="filter.filterId?.id"
                      lines="two"
                    >
                      <template v-slot:title>{{ filter.filterName }}</template>
                      <template v-slot:subtitle>{{ filter.filterDescription }}</template>
                      <template v-slot:append>
                        <v-tooltip :text="$t('admin-resources.filter.savedFilters.chooseFilter')" location="top">
                          <template v-slot:activator="{ props: tooltipProps }">
                            <v-btn icon variant="text" v-bind="tooltipProps" @click="applySavedFilter(filter)">
                              <v-icon>mdi-filter-check</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </template>
                    </v-list-item>
                    <v-list-item v-if="filteredMenuFilters.length === 0">
                      <v-list-item-title class="text-medium-emphasis">{{ $t('admin-resources.filter.savedFilters.noFilters') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn variant="outlined" @click="savedFiltersDialog = false">{{ $t('confirm-dialog.cancel') }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <form>
                <v-container>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="resourceListFilter.data.uuid"
                        clearable
                        hide-details
                        density="compact"
                        :label="$t('admin-resources.filter.uuid')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="resourceListFilter.data.resourceName"
                        clearable
                        hide-details
                        density="compact"
                        :label="$t('admin-resources.filter.name')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="resourceListFilter.data.resourceExternalId"
                        clearable
                        hide-details
                        density="compact"
                        :label="$t('admin-resources.filter.externalId')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="resourceListFilter.data.resourceStatus"
                        clearable
                        hide-details
                        density="compact"
                        :items="statusOptions"
                        :label="$t('admin-resources.filter.status')"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="resourceListFilter.data.resourceType"
                        clearable
                        hide-details
                        density="compact"
                        :items="resourceTypeOptions"
                        :label="$t('admin-resources.filter.resourceType')"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center gap-2">
                      <v-btn variant="outlined" @click="addFilterPropertyRow">{{ $t('admin-resources.filter.addFilterProperty') }}</v-btn>
                      <v-btn variant="outlined" class="ml-2" @click="savedFiltersDialog = true">{{ $t('admin-resources.filter.useExistingFilter') }}</v-btn>
                    </v-col>
                  </v-row>

                  <!-- Lista propertiesów służących do filtrowania -->
                  <resource-properties-edit-component
                    :allPropertiesDictionary="searchPropertiesDictionary"
                    :propertiesList="resourceListFilter.properties"
                  />

                  <v-row justify="start">
                    <v-col cols="12">
                      <v-btn variant="outlined" class="mr-4" @click="listResources">{{ $t('admin-resources.filter.search') }}</v-btn>
                      <v-btn variant="outlined" @click="clearFilters">{{ $t('admin-resources.filter.clear') }}</v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </form>

              <!-- dialog z podglądem resource'a -->
              <resource-preview-dialog v-if="resourcePreviewDialog"
                :resourceId="resourcePreviewId"
                :previewDialog="resourcePreviewDialog"
                :previewSrc="resourcePreviewSrc"
                :previewTitle="resourcePreviewTitle"
                @closeDialog="onCloseResourcePreview"
              />
            </template>

            <template v-slot:item.name="{ item }">
              <v-tooltip location="top" open-delay="500">
                <template v-slot:activator="{ props }">
                  <div v-bind="props" class="text-wrap" style="max-width: 500px">
                    {{ item.name }}
                  </div>
                </template>
                <span>{{ item.name }}</span>
              </v-tooltip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-icon size="small" class="mr-2" @click="showResourcePreview(item)"> mdi-chevron-right-circle-outline </v-icon>
              <v-icon size="small" class="mr-2" @click="copyIdToClipboard(item)"> mdi-content-copy </v-icon>
            </template>

            <template v-slot:item.modifiedTimestamp="{ item }">
              <span>{{ new Date(item.modifiedTimestamp).toLocaleString(currentLanguage) }}</span>
            </template>
          </v-data-table-server>
        </div>
      </v-card-text>

      <v-card-actions class="pt-3">
        <v-spacer></v-spacer>
        <v-btn v-if="!dialogOptions.noconfirm" variant="outlined" @click="resolveCancel">
          {{ $t('confirm-dialog.cancel') }}
        </v-btn>

        <v-btn variant="flat" color="primary" @click="resolveSelectedResource">{{ $t('confirm-dialog.ok') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, inject, nextTick } from 'vue';
import { useI18n } from '@/shared/composables/i18n';
import type {
  AdminResourceDictPropertiesListRequest,
  AdminResourcesListRequest,
  ResourceDictPropertyValue,
  ResourceDto,
  ResourceFilterCriteriaValue,
  ResourceFilterValue,
  ResourceId,
} from '@/venit/services/model/techpubs-cms';
import { ResourceStatus, ResourceType } from '@/venit/services/model/techpubs-cms';
import ResourceManagementService from '@/venit/admin/resource-management/resource-management.service';
import ResourcePreviewDialog from '@/venit/components/resources/resource-preview-dialog.vue';
import { TablePage } from '@/shared/TablePage';
import { ObjectHelper } from '@/object-helper';
import debounce from 'debounce';
import FiltersManagementService from '@/venit/admin/filters-management/filters-management.service';
import ResourcePropertiesEditComponent from '@/venit/admin/resource-management/resource-properties-edit-component.vue';
import { useTranslationStore } from '@/stores';

export default defineComponent({
  name: 'ResourcePickerDialogComponent',

  components: {
    'resource-preview-dialog': ResourcePreviewDialog,
    'resource-properties-edit-component': ResourcePropertiesEditComponent,
  },

  setup() {
    const { t: $t } = useI18n();

    const resourcesManagementService = inject<() => ResourceManagementService>('resourceService')!;
    const filtersManagementService = inject<() => FiltersManagementService>('filtersService')!;
    const translationStore = useTranslationStore();

    // Helper function
    const createDefaultFilterCriteria = (): ResourceFilterCriteriaValue => ({
      data: {
        uuid: null,
        resourceName: null,
        resourceExternalId: null,
        resourceType: ResourceType.FILE,
        resourceStatus: ResourceStatus.PUBLISHED,
        resourceStatusList: [],
      },
      properties: [],
      contentInfo: [],
    });

    // Dialog state
    const dialog = ref(false);
    const message = ref<string | null>(null);
    const title = ref<string | null>(null);
    const dialogOptions = ref({
      color: 'grey lighten-3',
      noconfirm: false,
    });

    // Promise resolvers
    let resolvePromise: ((v: ResourceDto | null) => void) | null = null;
    let rejectPromise: ((v: ResourceDto | null) => void) | null = null;

    // Table state
    const loading = ref(true);
    const resourceListFilter = ref<ResourceFilterCriteriaValue>(createDefaultFilterCriteria());
    const headers = computed(() => [
      { title: $t('admin-resources.table.header.resourceName'), key: 'name' },
      { title: $t('admin-resources.table.header.externalId'), key: 'externalId' },
      { title: $t('admin-resources.table.header.resourceType'), key: 'type' },
      { title: $t('admin-resources.table.header.status'), key: 'status' },
      { title: $t('admin-resources.table.header.modifiedOn'), key: 'modifiedTimestamp' },
      { title: $t('admin-resources.table.header.actions'), key: 'actions', sortable: false },
    ]);
    const resources = ref<ResourceDto[]>([]);
    const totalResources = ref(0);
    const resourcesTableOptions = ref<TablePage>(new TablePage(10));
    const selectedResource = ref<string[]>([]);

    // Preview dialog state
    const resourcePreviewId = ref<ResourceId | null>(null);
    const resourcePreviewDialog = ref(false);
    const resourcePreviewSrc = ref<string | null>(null);
    const resourcePreviewTitle = ref('Resource preview');

    // Options
    const statusOptions = Object.keys(ResourceStatus);
    const resourceTypeOptions = Object.keys(ResourceType);

    // Properties dictionary
    const searchPropertiesDictionary = ref<ResourceDictPropertyValue[]>([]);

    // Filters
    const allMenuFilters = ref<ResourceFilterValue[]>([]);
    const selectedMenuFilterId = ref<ResourceFilterValue | null>(null);
    const savedFiltersDialog = ref(false);
    const savedFiltersSearch = ref('');

    // Computed
    const currentLanguage = computed(() => translationStore.currentLanguage);
    const filteredMenuFilters = computed(() => {
      const q = savedFiltersSearch.value?.trim().toLowerCase();
      if (!q) return allMenuFilters.value;
      return allMenuFilters.value.filter(f =>
        f.filterName?.toLowerCase().includes(q) || f.filterDescription?.toLowerCase().includes(q)
      );
    });

    // Methods
    const createFilterValueFromCriteria = (): ResourceFilterValue => {
      const resourceFilterCriteria: ResourceFilterCriteriaValue = ObjectHelper.deepCopy(resourceListFilter.value);
      resourceFilterCriteria.properties = resourceListFilter.value.properties.filter(prop => prop.propertyCode != null);

      return {
        filterId: null,
        filterName: null,
        filterDescription: null,
        filterType: null,
        criteria: resourceFilterCriteria,
        filterGroupingType: null,
        updatedOn: null,
        updatedBy: null,
      };
    };

    const listResourcesImpl = () => {
      loading.value = true;

      const filterValue = createFilterValueFromCriteria();

      const listRequest: AdminResourcesListRequest = {
        page: resourcesTableOptions.value,
        filter: filterValue,
        lang: currentLanguage.value,
      };

      resourcesManagementService()
        .listResources(listRequest)
        .then(response => {
          resources.value = response.data.resourcesList.pageContent;
          totalResources.value = response.data.resourcesList.totalElements;
          loading.value = false;
        });
    };

    const listResources = debounce(listResourcesImpl, 300);

    const listAllMenuFilters = () => {
      filtersManagementService()
        .listAllMenuFilters()
        .then(response => {
          allMenuFilters.value = response.data.filters;
        });
    };

    const clearFilters = () => {
      resourceListFilter.value = createDefaultFilterCriteria();
      selectedMenuFilterId.value = null;
    };

    const addFilterPropertyRow = () => {
      resourceListFilter.value.properties.push({
        options: [],
        propertyCode: null,
      });
    };

    const showResourcePreview = (item: ResourceDto) => {
      resourcePreviewSrc.value = item.path;
      resourcePreviewTitle.value = item.name;
      resourcePreviewDialog.value = true;
      resourcePreviewId.value = { uuid: item.uuid };
    };

    const onCloseResourcePreview = () => {
      resourcePreviewDialog.value = false;
      nextTick(() => {
        resourcePreviewSrc.value = null;
        resourcePreviewTitle.value = '';
      });
    };

    const copyIdToClipboard = (item: ResourceDto) => {
      navigator.clipboard.writeText(item.uuid);
    };

    const onSelectedSavedFilter = () => {
      if (!selectedMenuFilterId.value) {
        return;
      }
      applySavedFilter(selectedMenuFilterId.value);
    };

    const applySavedFilter = (filter: ResourceFilterValue) => {
      const emptyFilterCriteria = createDefaultFilterCriteria();

      if (filter.criteria?.properties) {
        emptyFilterCriteria.properties = ObjectHelper.deepCopy(filter.criteria.properties);
      }
      if (filter.criteria?.data) {
        emptyFilterCriteria.data = ObjectHelper.deepCopy(filter.criteria.data);
      }
      resourceListFilter.value = emptyFilterCriteria;
      savedFiltersDialog.value = false;
    };

    const openDefault = (resourceId: ResourceId): Promise<ResourceDto | null> => {
      return open('Select resource', null, {}, resourceId);
    };

    const openWithFilter = (filter: ResourceFilterValue): Promise<ResourceDto | null> => {
      dialog.value = true;
      title.value = filter.filterName;
      message.value = null;
      dialogOptions.value = { color: 'grey lighten-3', noconfirm: false };

      const criteria = createDefaultFilterCriteria();
      if (filter.criteria?.data) {
        criteria.data = ObjectHelper.deepCopy(filter.criteria.data);
      }
      if (filter.criteria?.properties) {
        criteria.properties = ObjectHelper.deepCopy(filter.criteria.properties);
      }
      resourceListFilter.value = criteria;

      return new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });
    };

    const open = (titleText: string, messageText: string | null, options: any, resourceId: ResourceId): Promise<ResourceDto | null> => {
      dialog.value = true;
      title.value = titleText;
      message.value = messageText;
      dialogOptions.value = Object.assign(dialogOptions.value, options);
      resourceListFilter.value.data.uuid = resourceId?.uuid;

      return new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });
    };

    const resolveSelectedResource = () => {
      const selectedUuid = selectedResource.value.length > 0 ? selectedResource.value[0] : null;
      const selected = selectedUuid ? resources.value.find(r => r.uuid === selectedUuid) ?? null : null;
      if (resolvePromise) {
        resolvePromise(selected);
      }
      dialog.value = false;
    };

    const resolveCancel = () => {
      if (resolvePromise) {
        resolvePromise(null);
      }
      dialog.value = false;
    };

    // Watchers
    watch(resourcesTableOptions, () => {
      listResources();
    }, { deep: true });

    watch(resourceListFilter, () => {
      resourcesTableOptions.value.page = 1;
      listResources();
    }, { deep: true });

    // Initialize
    const request: AdminResourceDictPropertiesListRequest = {
      criteria: {
        code: null,
        description: null,
      },
    };

    resourcesManagementService()
      .listPropertiesDict(request, currentLanguage.value)
      .then(response => {
        searchPropertiesDictionary.value = response.data.resourceProperties;
      });

    listAllMenuFilters();

    return {
      // State
      dialog,
      message,
      title,
      dialogOptions,
      loading,
      resourceListFilter,
      headers,
      resources,
      totalResources,
      resourcesTableOptions,
      selectedResource,
      resourcePreviewId,
      resourcePreviewDialog,
      resourcePreviewSrc,
      resourcePreviewTitle,
      statusOptions,
      resourceTypeOptions,
      searchPropertiesDictionary,
      allMenuFilters,
      selectedMenuFilterId,
      savedFiltersDialog,
      savedFiltersSearch,
      filteredMenuFilters,
      currentLanguage,
      // Methods
      listResources,
      clearFilters,
      addFilterPropertyRow,
      applySavedFilter,
      showResourcePreview,
      onCloseResourcePreview,
      copyIdToClipboard,
      onSelectedSavedFilter,
      openDefault,
      open,
      openWithFilter,
      resolveSelectedResource,
      resolveCancel,
    };
  },
});
</script>
